import moment from 'moment-timezone';

export const DEFAULT_TIMEZONE = 'America/Chicago';

export const FREQUENT_TIMEZONE_GROUP = 'Frequently used';

/** Pinned first, and only when the API list actually includes them. */
export const FREQUENT_TIMEZONES = [
  'Etc/UTC',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/New_York',
  'America/Winnipeg',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/Kentucky/Louisville',
] as const;

const FREQUENT_TIMEZONE_SET = new Set<string>(FREQUENT_TIMEZONES);

type MomentTimezoneData = {
  _links?: Record<string, string>;
  _zones?: Record<string, unknown>;
  _names?: Record<string, string>;
};

const tzData = moment.tz as typeof moment.tz & MomentTimezoneData;

/**
 * moment-timezone 0.5 stores links in both directions. Canonical zones also have
 * packed data in `_zones`; aliases only appear in `_links` until they are first loaded.
 * Snapshot this at import time, before any zone lookup materializes aliases.
 */
const canonicalByAlias = new Map<string, string>();
const aliasIds = new Set<string>();

(() => {
  const links = tzData._links ?? {};
  const zones = tzData._zones ?? {};
  const names = tzData._names ?? {};

  for (const key of Object.keys(links)) {
    if (zones[key] != null) continue;
    const alias = names[key];
    const canonical = names[links[key]];
    if (!alias || !canonical) continue;
    aliasIds.add(alias);
    canonicalByAlias.set(alias, canonical);
  }
})();

/** Resolve a legacy alias (`Canada/Central`) to its canonical id (`America/Winnipeg`). */
export const toCanonicalTimezone = (timezone: string): string => {
  const fromLink = canonicalByAlias.get(timezone);
  if (fromLink) return fromLink;
  const normalized = timezone.toLowerCase().replace(/\//g, '_');
  return tzData._names?.[normalized] ?? timezone;
};

const isSelectableTimezone = (timezone: string): boolean =>
  timezone.includes('/') && !aliasIds.has(timezone);

/** America first, then the other regions in alphabetical order. */
const compareTimezones = (a: string, b: string): number => {
  const regionA = a.split('/')[0];
  const regionB = b.split('/')[0];
  if (regionA !== regionB) {
    if (regionA === 'America') return -1;
    if (regionB === 'America') return 1;
  }
  return a.localeCompare(b);
};

/** Drop legacy aliases and non-region ids, then pin frequently used zones. */
export const toSelectableTimezones = (timezones: string[]): string[] => {
  const selectable = new Set(timezones.filter(isSelectableTimezone));
  const frequent = FREQUENT_TIMEZONES.filter(timezone => selectable.has(timezone));
  const rest = Array.from(selectable)
    .filter(timezone => !FREQUENT_TIMEZONE_SET.has(timezone))
    .sort(compareTimezones);
  return [...frequent, ...rest];
};

export const timezoneGroup = (timezone: string): string => {
  if (FREQUENT_TIMEZONE_SET.has(timezone)) return FREQUENT_TIMEZONE_GROUP;
  return timezone.split('/')[0] || timezone;
};

/** Prefer the browser's canonical zone when it is in the allowlist; otherwise use the API default. */
export const resolveDefaultTimezone = (timezones: string[]): string => {
  const guessed = moment.tz.guess();
  const canonical = toCanonicalTimezone(guessed);
  if (timezones.includes(canonical)) return canonical;
  if (timezones.includes(guessed)) return guessed;
  return DEFAULT_TIMEZONE;
};

/**
 * Keep edit-form selects valid when the stored value is legacy or not yet loaded.
 * A frequent id stays in that group. Anything else is inserted among the regions
 * so one region does not get two headers.
 */
export const withMissingTimezone = (timezones: string[], value?: string): string[] => {
  if (!value || timezones.includes(value)) {
    return timezones;
  }
  if (FREQUENT_TIMEZONE_SET.has(value)) {
    return [value, ...timezones];
  }

  const restStart = timezones.findIndex(timezone => !FREQUENT_TIMEZONE_SET.has(timezone));
  const splitAt = restStart === -1 ? timezones.length : restStart;
  const rest = timezones.slice(splitAt);
  let index = 0;
  while (index < rest.length && compareTimezones(rest[index], value) < 0) {
    index += 1;
  }

  return [...timezones.slice(0, splitAt), ...rest.slice(0, index), value, ...rest.slice(index)];
};
